// BottomNavigation.jsx
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { matchPath, useLocation } from 'react-router';
import { withTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import { removeLangFromPath } from '../resource/langPath.js';

import NeedAuth from '../container/NeedAuth.js';
import MeAvatar from '../container/MeAvatar.js';

import Delay from '../resource/Delay.js';
import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { ButtonId } from '../resource/mixpanel.js';
import withNotificationPaddingMonitor from '../resource/withNotificationPaddingMonitor.js';
import { SHORT_PATHS } from '../resource/getShouldShowFooter.js';
import getNavigationIcon, {
  NavigationId,
} from '../resource/getNavigationIcon.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import {
  color,
  textColor,
  headerHeight,
  browseTabHeight,
  footerNavigationHeight,
  iPhoneXSeriesFooterPaddingInPWA,
} from '../style/variables.js';
import { HomeCategoryUi } from '../resource/feedConstants.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import Spinning from '../style/Spinning.js';
import media from '../style/media.js';

import NewBadgeIconSource from '../../img/img_badge_new.svg';
import AddIconSource from '../../img/ic_add_white_xs.svg';
import ActiveRetryIcon from '../../img/ic_retry_active.svg';

import usePreviousValue from '../hook/usePreviousValue.js';
import { useTranslation } from 'react-i18next';
import { MISSION_TOOLTIP_SEEN_KEY } from '../resource/missionConstants.js';
import MissionTooltip from '../component/MissionTooltip.jsx';

const ICON_SIZE = 28; // TODO: remote config
const MAX_MILLISECONDS = 500; // TODO: remote config
const SCROLL_ANIMATION_DURATION_MSEC = 500;
const REFRESH_ICON_ANIMATION_DURATION_MSEC = 1000;
const randomMilliseconds = Math.floor(Math.random(0, 1) * MAX_MILLISECONDS);
const TAB_CONFIG = {
  explore: {
    id: NavigationId.HOME,
    linkTo: '/',
    pathArray: [
      '/',
      '/home',
      '/freezone',
      '/story',
      '/explore',
      '/following',
      '/category-overview',
    ],
    isNeedAuth: false,
    buttonId: ButtonId.Tab.TabButtonHome,
    buttonNameI18nKey: 'tabbar_home',
  },
  livestream: {
    id: 'livestream',
    linkTo: '/livestream',
    pathArray: ['/livestream'],
    isNeedAuth: false,
    buttonId: ButtonId.Tab.TabButtonLivestream,
    buttonNameI18nKey: 'button_entrance_stream',
  },
  // event: {
  //   id: 'event',
  //   linkTo: '/events/lottery',
  //   pathArray: ['/events'],
  //   isNeedAuth: false,
  //   buttonId: ButtonId.Tab.TabButtonEvent,
  //   buttonNameI18nKey: 'tab_event',
  // },
  // search: {
  //   id: 'search',
  //   linkTo: '/search/all',
  //   pathArray: ['/search/flix', '/search'],
  //   isNeedAuth: false,
  //   buttonId: ButtonId.Tab.TabButtonSearch,
  //   buttonNameI18nKey: 'tabbar_discover',
  // },
  video: {
    id: NavigationId.VIDEO,
    linkTo: '/video',
    pathArray: ['/video'],
    isNeedAuth: false,
    buttonId: ButtonId.Tab.TabButtonDiscover,
    buttonNameI18nKey: 'tabbar_discover',
  },
  shorts: {
    id: NavigationId.SHORTS,
    linkTo: '/shorts',
    pathArray: SHORT_PATHS,
    isNeedAuth: false,
    buttonId: ButtonId.Tab.TabButtonShorts,
    buttonNameI18nKey: 'shorts',
  },
  // shop: { id: 'shop' },
  // {
  //   id: 'create',
  //   buttonId: ButtonId.Tab.TabButtonCreate,
  // },
  chat: {
    id: NavigationId.CHAT,
    pathArray: ['/chat'],
    isNeedAuth: false,
    buttonId: ButtonId.Tab.TabButtonChat,
    buttonNameI18nKey: 'tabbar_chat',
  },
  me: {
    id: NavigationId.ME,
    pathArray: [
      '/u/:username',
      '/u/:username/:listType',
      '/u/:username/:listType/:publishStatus',
      '/user/:userId',
      '/user/:userId/:listType',
      '/user/:userId/:listType/:publishStatus',
    ],
    isNeedAuth: false,
    buttonId: ButtonId.Tab.TabButtonHamburger,
    buttonNameI18nKey: 'tabbar_my',
  },
};

export const BottomNavigation = ({
  meId = '',
  meUsername = '',
  hasUnread = false,
  isAccountLinksFetching = false,
  isAccountLinksFetched = false,
  isAuthed = false,
  hasUnsetItemOnManageProfile = true,
  isShortEnabled = true,
  login = () => null,
  fetchAccountLinks: getAccountLinks = () => null,
  fetchUnreadChatrooms = () => null,
  apiCacheTimestamp = 0,
  latestFeedRenewUnix = 0,
  isHamburgerMenuOpen = false,
  openHamburgerMenu = () => null,
  openUploadMenuModal = () => null,
  closeHamburgerMenu = () => null,
  fetchFeeds = () => null,
  renewFeedId = null,
  feedNameWithQuery = null,
  footerTabs = [],
  clipCategory = null,
  subscribeFeedChannel = () => null,
  unsubscribeFeedChannel = () => null,
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const cleanPathname = removeLangFromPath(pathname);
  const [isServer, setIsServer] = useState(true);
  const [isRefreshIconAnimating, setIsRefreshIconAnimating] = useState(false);
  const [isScrolledPastLivestreamFeed, setIsScrolledPastLivestreamFeed] =
    useState(false);
  const [shouldRefreshLivestreamFeed, setShouldRefreshLivestreamFeed] =
    useState(false);
  const [visibleTooltip, setVisibleTooltip] = useState(false);

  const prevApiCacheTimestamp = usePreviousValue(apiCacheTimestamp);
  const clipCategoryRef = useRef(clipCategory);
  const prevRenewFeedIdMap = useRef({});
  const showRefreshButtonTopOffset =
    headerHeight + cleanPathname === '/' ? browseTabHeight + 12 : 0; // 12px is the gap between browse tab and header text

  const checkScrollPosition = useCallback(() => {
    const livestreamFeedRefs = document.querySelectorAll(
      `[data-category_ui="${HomeCategoryUi.LIVESTREAM_SQUARE}"], [data-category_ui="${HomeCategoryUi.LIVESTREAM_GRID}"]`
    );

    if (livestreamFeedRefs.length > 0) {
      // get the last livestream feed
      const lastLivestreamFeedRef =
        livestreamFeedRefs[livestreamFeedRefs.length - 1];
      const scrollPastBuffer = window.innerHeight / 3;

      if (
        lastLivestreamFeedRef.getBoundingClientRect().top + scrollPastBuffer <
        showRefreshButtonTopOffset
      ) {
        // show the refresh button
        // if user scroll past the last livestream feed + 1/3 of the screen height
        setIsScrolledPastLivestreamFeed(true);
      } else if (
        lastLivestreamFeedRef.getBoundingClientRect().top >
        showRefreshButtonTopOffset
      ) {
        // hide the refresh button
        // if user scroll up to other feeds
        setIsScrolledPastLivestreamFeed(false);
      }
    }
  }, [showRefreshButtonTopOffset]);

  // Subscribe/Unsubscribe clip feed channel
  useEffect(() => {
    if (clipCategory) {
      clipCategoryRef.current = clipCategory;
      subscribeFeedChannel({ feedName: clipCategory });
    }
    return () => {
      unsubscribeFeedChannel({ feedName: clipCategoryRef.current });
    };
  }, [clipCategory, subscribeFeedChannel, unsubscribeFeedChannel]);

  useEffect(() => {
    const nextTick = setTimeout(() => {
      setIsServer(false);
    }, 0);

    return () => {
      clearTimeout(nextTick);
    };
  }, []);

  useEffect(() => {
    const fetchAccountLinks = () => {
      if (!isAccountLinksFetched && !isAccountLinksFetching) {
        getAccountLinks();
      }
    };

    const milliSecs =
      apiCacheTimestamp !== prevApiCacheTimestamp ? randomMilliseconds : 0;

    const nextTick = setTimeout(() => {
      if (isAuthed) {
        fetchUnreadChatrooms({ page: 1 });
        fetchAccountLinks();
      }
    }, milliSecs);

    return () => {
      clearTimeout(nextTick);
    };
  }, [
    isAuthed,
    isAccountLinksFetched,
    isAccountLinksFetching,
    getAccountLinks,
    fetchUnreadChatrooms,
    prevApiCacheTimestamp,
    apiCacheTimestamp,
  ]);

  useEffect(() => {
    if (renewFeedId && prevRenewFeedIdMap.current[pathname] !== renewFeedId) {
      setShouldRefreshLivestreamFeed(true);
    }

    checkScrollPosition();
  }, [prevRenewFeedIdMap, pathname, renewFeedId, checkScrollPosition]);

  useEffect(() => {
    checkScrollPosition();

    window.addEventListener('scroll', checkScrollPosition);

    return () => {
      window.removeEventListener('scroll', checkScrollPosition);
    };
  }, [checkScrollPosition]);

  // Show/hidden tooltip
  useEffect(() => {
    if (!isAuthed || !meId) return;

    const seenUsers = JSON.parse(
      localStorage.getItem(MISSION_TOOLTIP_SEEN_KEY) || '{}'
    );

    if (!seenUsers[meId]) {
      setVisibleTooltip(true);
    }
  }, [isAuthed, meId]);

  const handleRefresh = useCallback(async () => {
    const livestreamFeedRefs = document.querySelectorAll(
      `[data-category_ui="${HomeCategoryUi.LIVESTREAM_SQUARE}"], [data-category_ui="${HomeCategoryUi.LIVESTREAM_GRID}"]`
    );

    if (livestreamFeedRefs.length > 0) {
      // get the last livestream feed
      const lastLivestreamFeedRef =
        livestreamFeedRefs[livestreamFeedRefs.length - 1];

      // scroll to the last livestream feed
      lastLivestreamFeedRef.scrollIntoView({
        behavior: 'smooth',
      });
    }
    setIsRefreshIconAnimating(true);

    // update the renewFeedId for the current pathname
    setShouldRefreshLivestreamFeed(false);
    prevRenewFeedIdMap.current[pathname] = renewFeedId;

    await new Delay(SCROLL_ANIMATION_DURATION_MSEC).promise();
    // wait for scroll animation to complete (500ms should be enough for most cases)
    fetchFeeds({
      id: renewFeedId,
      type: feedNameWithQuery,
      page: 1,
      unixTimestamp: latestFeedRenewUnix,
      shouldSkipLocalCache: true,
    });

    await new Delay(
      REFRESH_ICON_ANIMATION_DURATION_MSEC - SCROLL_ANIMATION_DURATION_MSEC
    ).promise();
    setIsRefreshIconAnimating(false);
  }, [
    pathname,
    renewFeedId,
    feedNameWithQuery,
    latestFeedRenewUnix,
    fetchFeeds,
  ]);

  const handleRefreshButtonClick = useMemo(
    () =>
      debounce(handleRefresh, REFRESH_ICON_ANIMATION_DURATION_MSEC, {
        leading: true,
        trailing: false,
      }),
    [handleRefresh]
  );

  const renderAvatarMenuIcon = ({ isMatch }) => {
    return (
      <MenuIconWrapper isMatch={isMatch}>
        <img
          src={getNavigationIcon({
            navigationId: NavigationId.ME,
            isAuthed: true,
            isMatch,
          })}
          aria-hidden
        />
      </MenuIconWrapper>
    );
  };

  const renderAvatarItem = ({ id, pathArray, buttonId, buttonNameI18nKey }) => {
    const isMatch = pathArray.some(path => {
      const matched = matchPath(path, cleanPathname);
      if (matched) {
        const { userId, username } = matched.params;
        return userId === meId || username === meUsername;
      }
      return false;
    });

    return (
      <Item key={id}>
        <ItemContent
          $isActive={isMatch || isHamburgerMenuOpen}
          data-element_id={buttonId}
          onClick={() => {
            isHamburgerMenuOpen ? closeHamburgerMenu() : openHamburgerMenu();
          }}
        >
          {isAuthed ? (
            <Avatar $isActive={isMatch || isHamburgerMenuOpen}>
              <MissionTooltip
                visible={visibleTooltip}
                onClose={() => setVisibleTooltip(false)}
                placement="bottomNav"
              >
                {t('tips_my_center_update', {
                  ns: TranslationNamespace.BACKPACK,
                })}
              </MissionTooltip>
              <MeAvatar
                size={24}
                alt={t(buttonNameI18nKey, {
                  ns: TranslationNamespace.GENERAL,
                })}
              />
              {renderAvatarMenuIcon({
                isMatch: isMatch || isHamburgerMenuOpen,
                id,
              })}
              {hasUnsetItemOnManageProfile && <AvatarRedDot />}
            </Avatar>
          ) : (
            <IconWrapper>
              <Icon
                src={getNavigationIcon({
                  navigationId: id,
                  isMatch: isMatch || isHamburgerMenuOpen,
                  hasUnread,
                })}
                alt={t(buttonNameI18nKey, {
                  ns: TranslationNamespace.GENERAL,
                })}
                aria-hidden
                width={ICON_SIZE}
                height={ICON_SIZE}
              />
            </IconWrapper>
          )}
          <MeItemText $isActive={isMatch || isHamburgerMenuOpen}>
            {t(buttonNameI18nKey, {
              ns: TranslationNamespace.GENERAL,
            })}
          </MeItemText>
        </ItemContent>
      </Item>
    );
  };

  const renderNavItem = ({
    id,
    linkTo,
    pathArray,
    linkTitle,
    isNeedAuth = true,
    buttonId,
    buttonNameI18nKey,
  }) => {
    const path = linkTo || `/${id}`;
    const isMatch = pathArray.some(path => matchPath(path, cleanPathname));
    const isRefreshSupported = ['/', '/livestream'].some(path =>
      matchPath(path, cleanPathname)
    );
    const content = (
      <IconWrapper>
        <Icon
          src={getNavigationIcon({
            navigationId: id,
            isMatch,
            hasUnread,
          })}
          alt={t(buttonNameI18nKey, {
            ns: TranslationNamespace.GENERAL,
          })}
          aria-hidden
          title={linkTitle || id}
          width={ICON_SIZE}
          height={ICON_SIZE}
        />
        {id === NavigationId.LIVESTREAM && (
          <NewBadge
            src={NewBadgeIconSource}
            width="24"
            height="15"
            alt="new badge"
          />
        )}
      </IconWrapper>
    );

    const link = isNeedAuth ? (
      <NeedAuth>
        <ItemContent
          $isActive={isMatch}
          data-element_id={buttonId}
          onClick={() => {
            isHamburgerMenuOpen && closeHamburgerMenu();
          }}
          as={Link}
          to={path}
          data-key="authed"
        >
          {content}
          <ItemText $isActive={isMatch}>
            {t(buttonNameI18nKey, {
              ns: TranslationNamespace.GENERAL,
            })}
          </ItemText>
        </ItemContent>
        <ItemContent data-key="authing" $isActive={isMatch}>
          {content}
          <ItemText $isActive={isMatch}>
            {t(buttonNameI18nKey, {
              ns: TranslationNamespace.GENERAL,
            })}
          </ItemText>
        </ItemContent>
        <ItemContent
          $isActive={isMatch}
          data-key="needAuth"
          data-element_id={buttonId}
          onClick={() => {
            return login();
          }}
        >
          {content}
          <ItemText $isActive={isMatch}>
            {t(buttonNameI18nKey, {
              ns: TranslationNamespace.GENERAL,
            })}
          </ItemText>
        </ItemContent>
      </NeedAuth>
    ) : (
      <ItemContent
        $isActive={isMatch}
        data-element_id={buttonId}
        onClick={() => {
          isHamburgerMenuOpen && closeHamburgerMenu();
        }}
        as={Link}
        to={path}
        data-key={id}
      >
        {content}
        <ItemText $isActive={isMatch}>
          {t(buttonNameI18nKey, {
            ns: TranslationNamespace.GENERAL,
          })}
        </ItemText>

        {/* refresh button for livestream feed */}
        {isMatch && isRefreshSupported && (
          <RefreshButton
            show={shouldRefreshLivestreamFeed && isScrolledPastLivestreamFeed}
            onClick={handleRefreshButtonClick}
          >
            <IconWrapper>
              <Icon
                src={ActiveRetryIcon}
                alt={t('refresh', {
                  ns: TranslationNamespace.SETTINGS,
                })}
                aria-hidden
                title="refresh"
                width={ICON_SIZE}
                height={ICON_SIZE}
                isAnimating={isRefreshIconAnimating}
              />
            </IconWrapper>
            <ItemText $isActive>
              {t('refresh', {
                ns: TranslationNamespace.SETTINGS,
              })}
            </ItemText>
          </RefreshButton>
        )}
      </ItemContent>
    );

    return <Item key={id}>{link}</Item>;
  };

  const renderCreateItem = ({ id, buttonId }) => (
    <Item key={id}>
      <CreateButton
        data-element_id={buttonId}
        onClick={() => {
          return openUploadMenuModal();
        }}
      >
        <img src={AddIconSource} alt="create button" />
      </CreateButton>
    </Item>
  );

  return (
    <StyledBottomNavigation
      isIphoneXSeriesPWA={!isServer && getIsIphoneXSeriesPWA()}
    >
      <List>
        {footerTabs.map(tab => {
          const item = TAB_CONFIG[tab] || {};

          if (!item.id) {
            return null;
          }

          switch (item.id) {
            case NavigationId.ME:
              return renderAvatarItem(item);
            case NavigationId.CREATE:
              return renderCreateItem(item);
            case NavigationId.SHORTS:
              return isShortEnabled ? renderNavItem(item) : null;
            default:
              return renderNavItem(item);
          }
        })}
      </List>
    </StyledBottomNavigation>
  );
};

const Avatar = styled.div`
  position: relative;
  border-radius: 50%;
  border-style: solid;
  border-width: 1px;
  border-color: ${({ $isActive }) =>
    $isActive ? color.tealBlue : color.black};
  width: ${ICON_SIZE}px;
  height: ${ICON_SIZE}px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  cursor: pointer;
`;

const AvatarRedDot = styled.div`
  position: absolute;
  top: -2px;
  right: -4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${color.red};
  border: solid 2px ${color.black};
`;

BottomNavigation.propTypes = {
  meId: PropTypes.string,
  meUsername: PropTypes.string,
  hasUnread: PropTypes.bool,
  isAccountLinksFetching: PropTypes.bool,
  isAccountLinksFetched: PropTypes.bool,
  isAuthed: PropTypes.bool,
  hasUnsetItemOnManageProfile: PropTypes.bool,
  isShortEnabled: PropTypes.bool,
  login: PropTypes.func,
  fetchAccountLinks: PropTypes.func,
  fetchUnreadChatrooms: PropTypes.func,
  apiCacheTimestamp: PropTypes.number,
  latestFeedRenewUnix: PropTypes.number,
  isHamburgerMenuOpen: PropTypes.bool,
  openHamburgerMenu: PropTypes.func,
  closeHamburgerMenu: PropTypes.func,
  openUploadMenuModal: PropTypes.func,
  fetchFeeds: PropTypes.func,
  feedNameWithQuery: PropTypes.string,
  footerTabs: PropTypes.array,
  renewFeedId: PropTypes.string,
  clipCategory: PropTypes.string,
  subscribeFeedChannel: PropTypes.func,
  unsubscribeFeedChannel: PropTypes.func,
};

const StyledBottomNavigation = styled.nav`
  border-top: 1px solid #232323;
  height: ${({ isIphoneXSeriesPWA }) =>
    isIphoneXSeriesPWA
      ? footerNavigationHeight + iPhoneXSeriesFooterPaddingInPWA
      : footerNavigationHeight}px;
  background-color: #191919;
  display: none;
  ${media.mobile`
    display: block;
  `};
`;

const List = styled.ul`
  display: flex;
  list-style: none;
  margin: 0px;
  padding: 0px;
  height: 48px;
`;

const Item = styled.li`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const ItemContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const IconWrapper = styled.div`
  position: relative;
  width: ${ICON_SIZE}px;
  height: ${ICON_SIZE}px;
`;

const Icon = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: pointer;

  ${({ isAnimating }) =>
    isAnimating &&
    css`
      animation: 1.3s ease ${Spinning};
    `}
`;

const ItemText = styled.div.attrs(({ $isActive }) => ({
  style: {
    color: $isActive ? color.tealBlue : textColor.white,
    fontWeight: $isActive ? 600 : 400,
  },
}))`
  margin-top: 2px;
  font-size: 12px;
`;

const MeItemText = styled(ItemText).attrs(({ $isActive }) => ({
  style: {
    color: $isActive ? color.tealBlue : textColor.white,
  },
}))``;

const CreateButton = styled.button`
  ${ResetButtonStyle};
  position: absolute;
  left: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translate(-50%, -8px);
  border-radius: 50%;
  background-color: ${color.tealBlue};

  > img {
    width: 32px;
    height: 32px;
  }
`;

const MenuIconWrapper = styled.div.attrs(({ isMatch }) => ({
  style: {
    backgroundColor: isMatch ? color.tealBlue : color.white,
  },
}))`
  position: absolute;
  bottom: -1px;
  right: -5px;
  border-radius: 50%;
  border: 1px solid ${color.black};
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  > img {
    width: 12px;
    height: 12px;
    object-fit: contain;
  }
`;

const NewBadge = styled.img`
  position: absolute;
  top: -10px;
  right: -7px;
  height: 15px;
  z-index: 1;
`;

const RefreshButton = styled.button`
  ${ResetButtonStyle}
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  background: #191919;
  opacity: 0;
  pointer-events: none;

  ${({ show }) =>
    show &&
    css`
      opacity: 1;
      pointer-events: auto;
    `}
`;

export default withTranslation()(
  withNotificationPaddingMonitor(BottomNavigation)
);
